/**
 * Table Parser Utility
 * BUG-20260224-180125: Shared utility for markdown table detection, parsing,
 * and student answer extraction for structured table input.
 * Modified by US-008: added 'multi-column' inputType for graphic-organiser questions
 * (type_14/15/16 rendered by US-007); extended TableRow.prefilled to (string | null)[]
 * so null encodes a blank cell the student fills and a string encodes a pre-filled read-only cell.
 */

/**
 * Represents a row in a parsed table
 */
export interface TableRow {
  /** Left column value — the prompt/label (e.g., "He (line 7)" or statement text) */
  rowLabel: string;
  /** True if this row is a pre-filled example (shown read-only) */
  isExample: boolean;
  /**
   * Pre-filled column values, one entry per FILLABLE column.
   * - single-column / true-false: only set for example rows (all-filled), all strings.
   * - multi-column (US-008): ALWAYS set. `null` = a blank cell the student fills;
   *   a string = a pre-filled read-only cell.
   */
  prefilled?: (string | null)[];
}

/**
 * Parsed table structure for rendering structured input forms
 */
export interface ParsedTable {
  /**
   * Determines input layout:
   *   'single-column' — one text field per row (type_10, type_3b)
   *   'true-false'    — dropdown + reason per row (type_8)
   *   'multi-column'  — one input per blank cell per row (type_14/15/16, US-008)
   */
  inputType: 'single-column' | 'true-false' | 'multi-column';
  /** All column headers from the markdown table */
  headers: string[];
  /** Headers for columns the student needs to fill in */
  fillableHeaders: string[];
  /** Table rows (both example and input rows) */
  rows: TableRow[];
}

/**
 * BUG-20260225-211448: Detect placeholder content that the tutor AI inserts in answer cells.
 * Placeholder strings like "?", "___", "..." indicate an empty-equivalent cell that the student
 * must fill in. Treating them as empty prevents all rows from being misclassified as examples. - 2026-02-25
 */
const PLACEHOLDER_STRINGS = new Set(['?', '??', '???', '_', '__', '___', '...', '-', '--', '---']);

/**
 * BUG-20260718-070000: Word-form blank markers the content generator writes into an
 * inline organiser table, e.g. "| How it looked | (blank) | Cleaned until ... |".
 * These are markers, never student-facing content — a real pre-filled organiser cell is a
 * descriptive phrase, not the bare word "blank"/"empty". Matched case-insensitively on the
 * whole cell, optionally wrapped in (), [] or <>. - 2026-07-18
 */
const BLANK_WORD_MARKER = /^[([<]?\s*(?:blank|empty)\s*[)\]>]?$/;

/** Any string composed only of ?, _, . or - characters. */
const PUNCTUATION_ONLY = /^[?_.-]+$/;

/**
 * BUG-20260718-070000: Leading item label on a blank cell, e.g. "(i) __________", "(a) ___",
 * "1. ___". Stripped before the placeholder test so the underscores are seen on their own.
 * Stripping is only a prelude to that test, so it can never reclassify real prose: "a. He was
 * tired" strips to "He was tired", which is still not a placeholder. - 2026-07-18
 */
const ITEM_LABEL_PREFIX = /^\(?\s*(?:[ivx]+|[a-z]|\d+)\s*[).:]\s*/;

/** True when a cell is empty-equivalent: a placeholder string, punctuation, or a blank marker. */
function isEmptyEquivalent(lower: string): boolean {
  return (
    PLACEHOLDER_STRINGS.has(lower) || PUNCTUATION_ONLY.test(lower) || BLANK_WORD_MARKER.test(lower)
  );
}

function isPlaceholderContent(text: string): boolean {
  const lower = text.toLowerCase();
  if (isEmptyEquivalent(lower)) return true;

  // Retry with a leading item label removed ("(i) ______" -> "______"). An all-label cell
  // ("(i)" alone) strips to empty, which is itself a blank cell in a fillable column.
  const unlabelled = lower.replace(ITEM_LABEL_PREFIX, '');
  return unlabelled !== lower && (unlabelled.length === 0 || isEmptyEquivalent(unlabelled));
}

/**
 * Detect if text contains a markdown table (pipe row + separator row)
 * Used by both MessageBubble (for rendering) and ConversationPanel (for input detection)
 */
export function hasMarkdownTable(text: string): boolean {
  return /\|.+\|/.test(text) && /\|[-: ]+\|/.test(text);
}

/**
 * US-008: Detect a graphic-organiser table (type_14/15/16) rendered by US-007.
 * Implements US-008 T2; enforces AC-008-6 detection priority.
 *
 * MUST be called ONLY after the existing isTrueFalse check fails (see parseMarkdownTable),
 * so an existing 3-column type_8 true/false table is never reclassified as multi-column.
 *
 * @param headers      raw header cells, e.g. ['Branch', 'Your answer']
 * @param headersLower headers lower-cased (reuse the array already built in parseMarkdownTable)
 */
function isMultiColumnTable(headers: string[], headersLower: string[]): boolean {
  // type_14 flowchart: header contains "branch" AND "your answer" (question_renderer.py:99).
  // Uses includes('your answer') with a space — specific enough to not collide with type_10/type_3b.
  const isFlowchart =
    headersLower.some((h) => h.includes('branch')) &&
    headersLower.some((h) => h.includes('your answer'));

  // type_15 grid / type_16 compare: more than two columns (question_renderer.py:126 / :165).
  const isWideGrid = headers.length > 2;

  return isFlowchart || isWideGrid;
}

/**
 * BUG-20260718-163555 (Workstream B, T-10, 2026-07-18): anchored whole-header match for
 * the canonical type_8 true/false column ("True or False?", "True/False", "True or False",
 * optionally trailing "?"). Replaces a bare substring test on 'true'/'false', which also
 * matched legitimate organiser column headers merely CONTAINING that substring (e.g.
 * "What was really true") and wrongly reclassified them as true-false tables. Verified
 * against every live type_8 header in prod/uat/dev (both the 3-column
 * "Statement | True or False? | Reason" and the 2-column P4 tick-only variant) and the
 * generator template (Templates/psle-question-types-rules.json:693) — zero live or
 * template-mandated type_8 header fails to match.
 */
const TRUE_FALSE_HEADER = /^true\s*(?:\/|or)\s*false\s*\??$/;

/** A line that is a GFM delimiter row, e.g. "| --- | :--- |". */
function isSeparatorLine(trimmed: string): boolean {
  return /^\|[\s-:]+\|$/.test(trimmed) || /^\|(\s*[-:]+[-:\s]*\|)+\s*$/.test(trimmed);
}

/**
 * Locate the header row (first pipe row that isn't a delimiter) and the delimiter row.
 * Either may be -1. Shared by parseMarkdownTable and normaliseMarkdownTable so both agree
 * on where a table starts.
 */
function locateTableLines(lines: string[]): {
  headerLineIndex: number;
  separatorLineIndex: number;
} {
  let headerLineIndex = -1;
  let separatorLineIndex = -1;

  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trim();
    if (!/^\|.+\|$/.test(trimmed)) continue;
    if (isSeparatorLine(trimmed)) {
      if (headerLineIndex >= 0 && separatorLineIndex < 0) {
        separatorLineIndex = i;
      }
    } else if (headerLineIndex < 0) {
      headerLineIndex = i;
    }
  }

  return { headerLineIndex, separatorLineIndex };
}

/**
 * BUG-20260718-070000: Repair a table that is missing its GFM delimiter row, for DISPLAY.
 *
 * parseMarkdownTable tolerates the omission, but remark-gfm does not — without a delimiter it
 * renders the table as raw pipe text. MessageBubble calls this so the student sees a real table
 * above the (now correct) input boxes. Returns the text unchanged when nothing needs repairing,
 * so well-formed content takes an identity path.
 */
export function normaliseMarkdownTable(text: string): string {
  const lines = text.split('\n');
  const { headerLineIndex, separatorLineIndex } = locateTableLines(lines);

  // Nothing to repair: no table, or the delimiter is already present.
  if (headerLineIndex < 0 || separatorLineIndex >= 0) return text;

  const headers = parsePipeRow(lines[headerLineIndex]);
  if (headers.length < 2) return text;

  // Only repair when a data row actually follows the header — otherwise this is a lone pipe
  // line, not a table, and injecting a delimiter would invent one.
  const next = (lines[headerLineIndex + 1] ?? '').trim();
  if (!/^\|.+\|$/.test(next)) return text;

  const delimiter = `|${headers.map(() => ' --- ').join('|')}|`;
  return [
    ...lines.slice(0, headerLineIndex + 1),
    delimiter,
    ...lines.slice(headerLineIndex + 1),
  ].join('\n');
}

/**
 * Parse a markdown table from message content into structured data.
 * Returns null if no valid table is found.
 *
 * Implements US-008 T3; enforces detection priority (DD-LOCAL-2):
 *   1. isTrueFalse  → 'true-false'   (UNCHANGED — runs FIRST, guards AC-008-6)
 *   2. isMultiColumnTable → 'multi-column' (NEW — only reached when NOT true-false)
 *   3. otherwise    → 'single-column' (existing fallback)
 */
export function parseMarkdownTable(text: string): ParsedTable | null {
  const lines = text.split('\n');
  const { headerLineIndex, separatorLineIndex } = locateTableLines(lines);

  // BUG-20260718-070000: Tolerate a missing "| --- | --- |" delimiter row. Some generated
  // questions omit it (prod: Passage-39 Q72/Q73), which left an otherwise well-formed table
  // unparsed and dropped the student to a single free-text box. When absent, data rows start
  // immediately after the header. GFM itself still needs the delimiter to render the table,
  // so normaliseMarkdownTable() repairs the text on the display side. - 2026-07-18
  if (headerLineIndex < 0) {
    return null;
  }
  const dataStartIndex = separatorLineIndex >= 0 ? separatorLineIndex + 1 : headerLineIndex + 1;

  // Parse headers
  const headers = parsePipeRow(lines[headerLineIndex]);
  if (headers.length < 1) {
    return null;
  }
  // BUG-20260727-162319: a genuine 1-column organiser table (header = the
  // organiser heading; each data row a bare item label like "(a)"/"(b)") is
  // handled by a dedicated early-return branch below, reusing the existing
  // 'single-column' inputType. This flag gates BOTH that branch and the
  // relaxed per-row cell-count check a few lines down — it must never relax
  // the general (>= 2 column) case.
  const isSingleColumnOrganiser = headers.length === 1;

  // Determine input type from headers
  const headersLower = headers.map((h) => h.toLowerCase());
  // BUG-20260718-163555 (T-10): anchored whole-header match (TRUE_FALSE_HEADER) replaces
  // the old bare substring test, which reclassified any organiser column header merely
  // CONTAINING 'true'/'false' (e.g. "What was really true") as a true-false table. The
  // statement+reason clause and the classification priority order (this check still runs
  // FIRST, guarding AC-008-6) are unchanged.
  const isTrueFalse =
    headersLower.some((h) => TRUE_FALSE_HEADER.test(h)) ||
    (headersLower.some((h) => h.includes('reason')) &&
      headersLower.some((h) => h.includes('statement')));

  // US-008 T3 Edit 1: collect raw cell rows (replaces inline TableRow building).
  // rawCellRows retains the original cell arrays so multi-column can inspect per-cell content.
  const rawCellRows: string[][] = [];
  for (let i = dataStartIndex; i < lines.length; i++) {
    const trimmed = lines[i].trim();
    if (!/^\|.+\|$/.test(trimmed)) {
      // Stop at first non-table line after data rows started
      if (rawCellRows.length > 0) break;
      continue;
    }

    const cells = parsePipeRow(trimmed);
    // BUG-20260727-162319: the per-row minimum is relaxed to 1 cell ONLY when
    // isSingleColumnOrganiser is set (headers.length === 1). Gating this on the
    // flag — rather than relaxing it globally — is load-bearing: a 2+-column
    // table with a stray malformed 1-cell data row must keep ignoring that row,
    // or parseMultiColumnTable would pad it with nulls and misalign every
    // subsequent row's grading.
    if (cells.length < (isSingleColumnOrganiser ? 1 : 2)) continue;
    rawCellRows.push(cells);
  }

  if (rawCellRows.length === 0) {
    return null;
  }

  // BUG-20260727-162319 (RC-002): a genuine 1-column organiser table — header
  // is the organiser heading, every data row is a single empty-or-item-label
  // cell (e.g. "(a)", "(b)") — is accepted here and returned as the EXISTING
  // 'single-column' inputType, so TableAnswerInput renders one labelled box per
  // row with ZERO component change. This branch returns EARLY, before the
  // isTrueFalse / isMultiColumnTable classification below (FP-3): a 1-column
  // table can satisfy neither of those predicates anyway (isTrueFalse needs a
  // matching header or a statement+reason pair; isMultiColumnTable needs
  // 'branch'+'your answer' headers or > 2 columns), so this ordering does not
  // disturb the existing AC-008-6 true-false-first priority. The isTrueFalse
  // const above is simply UNUSED on this path — it stays where it is (FP-3 /
  // ISS-L04), not relocated.
  //
  // The guard reuses isPlaceholderContent (TO-LL-032) as its ENTIRE
  // false-positive filter: a 1-column table of prose (e.g. a degenerate
  // type_8/type_10 mistake) fails the guard, and the branch returns null
  // (unchanged behaviour — that shape stays a free-text box, exactly as today).
  if (isSingleColumnOrganiser) {
    const isAcceptableOrganiser =
      rawCellRows.every((c) => c.length === 1) &&
      rawCellRows.every((c) => c[0].trim().length === 0 || isPlaceholderContent(c[0].trim()));

    if (!isAcceptableOrganiser) {
      return null;
    }

    // ISS-L05: rowLabel falls back to `Box ${i + 1}` when the trimmed cell is
    // EMPTY OR would DUPLICATE an earlier row's label. A duplicate is real:
    // isPlaceholderContent accepts bare punctuation placeholders, so two rows
    // both '| - |' would otherwise both be labelled '-', colliding on
    // TableAnswerInput.tsx's key={row.rowLabel} (duplicate React keys) and
    // collapsing parseStudentTableAnswer's rowLabel-keyed retry-prefill Map.
    const seen = new Set<string>();
    const rows: TableRow[] = rawCellRows.map((c, i) => {
      const raw = c[0].trim();
      const label = !raw || seen.has(raw) ? `Box ${i + 1}` : raw;
      seen.add(label);
      return { rowLabel: label, isExample: false };
    });
    return { inputType: 'single-column', headers, fillableHeaders: [], rows };
  }

  // Determine fillable headers (columns after the first — the label column)
  const fillableHeaders = headers.slice(1);

  // US-008 T3 Edit 2: classify, then build rows per layout.
  // Detection priority (DD-LOCAL-2, AC-008-6): true-false FIRST so type_8 is never reclassified.
  // Implements US-008; enforces AC-008-6 (type_8 true-false precedence).
  let inputType: ParsedTable['inputType'];
  if (isTrueFalse) {
    inputType = 'true-false';
  } else if (isMultiColumnTable(headers, headersLower)) {
    inputType = 'multi-column';
  } else {
    inputType = 'single-column';
  }

  let rows: TableRow[];
  if (inputType === 'multi-column') {
    rows = parseMultiColumnTable(fillableHeaders, rawCellRows);
  } else {
    // EXISTING single-column / true-false behaviour, unchanged.
    rows = rawCellRows.map((cells) => {
      const rowLabel = cells[0];
      const fillableCells = cells.slice(1);

      // BUG-20260225-211448: A row is an example only if ALL fillable cells have real content.
      // Placeholder strings like "?", "___", "..." are treated as empty so the row is
      // correctly classified as an input row, not an example row. - 2026-02-25
      const allFilled = fillableCells.every(
        (c) => c.trim().length > 0 && !isPlaceholderContent(c.trim())
      );

      return {
        rowLabel,
        isExample: allFilled,
        prefilled: allFilled ? fillableCells : undefined,
      };
    });
  }

  return { inputType, headers, fillableHeaders, rows };
}

/**
 * US-008 T3 Edit 3: Build TableRow[] for a multi-column organiser (type_14/15/16).
 * Implements AC-008-2 (shape) and CCM-004.
 *
 * Each row carries `prefilled` of length fillableHeaders.length:
 *   - null   = blank cell the student must fill (rendered as an <Input>)
 *   - string = pre-filled value (rendered read-only)
 * isExample is true only when every fillable cell is pre-filled (a fully-given row);
 * such rows are excluded from input rows by the component.
 *
 * nFillable is constant across all rows (every organiser row has headers.length - 1 fillable
 * cells). This constant width guarantees row-major alignment in parseStudentTableAnswer and
 * in the submit handler (DD-LOCAL-4).
 *
 * @param fillableHeaders headers after column 0 (the data columns)
 * @param dataRows        raw cell arrays including the column-0 label cell
 */
export function parseMultiColumnTable(fillableHeaders: string[], dataRows: string[][]): TableRow[] {
  const nFillable = fillableHeaders.length;
  return dataRows.map((cells) => {
    const rowLabel = cells[0];
    const fillableCells = cells.slice(1);
    const prefilled: (string | null)[] = [];
    for (let c = 0; c < nFillable; c++) {
      const raw = (fillableCells[c] ?? '').trim();
      // Reuse isPlaceholderContent so "?", "___", "..." count as blank.
      prefilled.push(raw.length > 0 && !isPlaceholderContent(raw) ? raw : null);
    }
    const isExample = prefilled.every((p) => p !== null);
    return { rowLabel, isExample, prefilled };
  });
}

/**
 * Parse a pipe-delimited row into cell values.
 * "| a | b | c |" → ["a", "b", "c"]
 */
function parsePipeRow(line: string): string[] {
  const trimmed = line.trim();
  // Remove leading and trailing pipes, split by pipe
  const inner = trimmed.replace(/^\|/, '').replace(/\|$/, '');
  return inner.split('|').map((cell) => cell.trim());
}

/**
 * Parse a student's previous text answer back into field values for pre-filling.
 * Returns a Map of rowLabel → array of column values.
 * Modified by US-008: added multi-column row-major retry branch (AC-008-5, DD-LOCAL-4).
 */
export function parseStudentTableAnswer(answer: string, table: ParsedTable): Map<string, string[]> {
  const result = new Map<string, string[]>();
  const inputRows = table.rows.filter((r) => !r.isExample);

  // US-008 T4: Multi-column retry pre-fill (AC-008-5).
  // Implements US-008; enforces DD-LOCAL-4 (row-major alignment over ALL fillable cells).
  // Do NOT filter empty lines — positions are load-bearing for alignment.
  //
  // BUG-20260718-163555 (T-5, RC-001, 2026-07-18): the submit handler (TableAnswerInput,
  // T-4) now emits ONLY blank-cell values, not every fillable cell — so this retry parser
  // must recognise TWO possible payload shapes and pick the right one by EXACT LINE COUNT
  // (deterministic: every blank cell is a single-line <Input>, so a value can never
  // contain a newline — DD-2):
  //   - NEW (blanks-only, post-fix submits): one line per BLANK cell, row-major.
  //   - LEGACY (all-cells, pre-fix messages already persisted, or an old cached client):
  //     one line per cell in the row-major table, pre-filled positions included.
  // When every input row is fully blank (e.g. type_14), the two counts coincide and both
  // readings produce identical output, so there is no ambiguous case.
  if (table.inputType === 'multi-column') {
    const nFillable = table.fillableHeaders.length;
    const lines = answer.split('\n');
    const nAll = inputRows.length * nFillable;
    let nBlank = 0;
    for (const row of inputRows) {
      for (const cell of row.prefilled ?? []) {
        if (cell === null) nBlank++;
      }
    }

    if (nBlank !== nAll && lines.length === nBlank) {
      // NEW blanks-only shape: consume one line per BLANK cell, row-major; pre-filled
      // positions take the table's own fixed value (so a retrying student still sees the
      // complete row, matching what she was shown originally).
      let lineIdx = 0;
      for (let r = 0; r < inputRows.length; r++) {
        const cells = inputRows[r].prefilled ?? [];
        const rowValues: string[] = [];
        for (let c = 0; c < nFillable; c++) {
          const cell = cells[c] ?? null;
          rowValues.push(cell !== null ? cell : (lines[lineIdx++] ?? '').trim());
        }
        result.set(inputRows[r].rowLabel, rowValues);
      }
      return result;
    }

    // LEGACY all-cells shape (also the fallback for any other/unrecognised line count —
    // identical to the pre-T-5 behaviour): flat row-major dump over EVERY fillable cell.
    // index = rowIndex * nFillable + colIndex.
    for (let r = 0; r < inputRows.length; r++) {
      const rowValues: string[] = [];
      for (let c = 0; c < nFillable; c++) {
        const idx = r * nFillable + c;
        rowValues.push(idx < lines.length ? lines[idx].trim() : '');
      }
      result.set(inputRows[r].rowLabel, rowValues);
    }
    return result;
  }

  if (table.inputType === 'true-false') {
    // Format: "1. True/False. reason text" or "1. True/False: reason text"
    const lines = answer.split('\n').filter((l) => l.trim().length > 0);

    for (let i = 0; i < Math.min(lines.length, inputRows.length); i++) {
      const line = lines[i].trim();
      // Match patterns like "1. True. reason" or "1. False: reason" or "True. reason"
      const match = line.match(/^(?:\d+\.\s*)?([Tt]rue|[Ff]alse)[.:]\s*(.*)/);
      if (match) {
        const tfValue = match[1].charAt(0).toUpperCase() + match[1].slice(1).toLowerCase();
        const reason = match[2].trim();
        result.set(inputRows[i].rowLabel, [tfValue, reason]);
      }
    }
  } else {
    // BUG-20260310-195233: Handle both new positional format and legacy label:value format - 2026-03-10
    // New format (post-fix): "challenge\nprinciples\nvaluable" — match by position (line N = input row N)
    // Old format (pre-fix):  "task: challenge\nideas: principles" — match by label prefix
    // Both formats must be supported for backward compatibility with existing conversation history.
    const lines = answer.split('\n').filter((l) => l.trim().length > 0);

    // Detect old "label: value" format by checking if any line has a colon whose
    // left-hand side matches a known input row label (exact or prefix match).
    const hasLabels = lines.some((line) => {
      const colonIndex = line.indexOf(':');
      if (colonIndex < 0) return false;
      const label = line.substring(0, colonIndex).trim();
      return inputRows.some(
        (r) => r.rowLabel === label || r.rowLabel.startsWith(label) || label.startsWith(r.rowLabel)
      );
    });

    if (hasLabels) {
      // Old format: parse by label matching (backward compatibility with pre-fix messages)
      for (const line of lines) {
        const colonIndex = line.indexOf(':');
        if (colonIndex < 0) continue;

        const label = line.substring(0, colonIndex).trim();
        const value = line.substring(colonIndex + 1).trim();

        const matchedRow = inputRows.find(
          (r) =>
            r.rowLabel === label || r.rowLabel.startsWith(label) || label.startsWith(r.rowLabel)
        );

        if (matchedRow) {
          result.set(matchedRow.rowLabel, [value]);
        }
      }
    } else {
      // New format: positional matching (line i → inputRows[i]) — same pattern as true-false branch
      for (let i = 0; i < lines.length && i < inputRows.length; i++) {
        result.set(inputRows[i].rowLabel, [lines[i].trim()]);
      }
    }
  }

  return result;
}
